import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Brain, Zap, DollarSign, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ModelConfig {
  name: string;
  model: string;
  temperature: number;
  maxTokens: number;
  topP: number;
  frequencyPenalty: number;
  presencePenalty: number;
}

export default function ModelSettings() {
  const { toast } = useToast();
  
  const [configs, setConfigs] = useState<Record<string, ModelConfig>>({
    mcqGeneration: {
      name: "MCQ Generation",
      model: "gpt-4o-mini",
      temperature: 0.7,
      maxTokens: 2000,
      topP: 0.9,
      frequencyPenalty: 0.2,
      presencePenalty: 0.1
    },
    explanation: {
      name: "Explanation Generation",
      model: "gpt-4o",
      temperature: 0.6,
      maxTokens: 1500,
      topP: 0.8,
      frequencyPenalty: 0.1,
      presencePenalty: 0.2
    },
    noteAssistant: {
      name: "Note Assistant",
      model: "gpt-4o-mini",
      temperature: 0.5,
      maxTokens: 1000,
      topP: 0.7,
      frequencyPenalty: 0.3,
      presencePenalty: 0.1
    },
    contentSuggestions: {
      name: "Content Suggestions",
      model: "gpt-4o",
      temperature: 0.8,
      maxTokens: 800,
      topP: 0.9,
      frequencyPenalty: 0.4,
      presencePenalty: 0.2
    }
  });

  const modelOptions = [
    { 
      value: "gpt-4o", 
      label: "GPT-4o", 
      cost: "$15.00/$30.00 per 1M tokens",
      speed: "Medium",
      quality: "High"
    },
    { 
      value: "gpt-4o-mini", 
      label: "GPT-4o Mini", 
      cost: "$0.15/$0.60 per 1M tokens",
      speed: "Fast",
      quality: "Good"
    }
  ];

  const updateConfig = (feature: string, field: string, value: any) => {
    setConfigs(prev => ({
      ...prev,
      [feature]: {
        ...prev[feature],
        [field]: value
      }
    }));
  };

  const saveConfigs = () => {
    // Here you would save to your backend/storage
    toast({
      title: "Settings Saved",
      description: "Model configurations have been updated successfully.",
    });
  };

  const resetToDefaults = () => {
    // Reset to default values
    toast({
      title: "Reset Complete",
      description: "All model settings have been reset to defaults.",
    });
  };

  const getModelInfo = (modelValue: string) => {
    return modelOptions.find(m => m.value === modelValue);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">AI Model Configuration</h3>
          <p className="text-sm text-muted-foreground">
            Configure AI models and parameters for different features
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefaults}>
            Reset Defaults
          </Button>
          <Button onClick={saveConfigs}>
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid gap-6">
        {Object.entries(configs).map(([feature, config]) => (
          <Card key={feature}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-4 w-4" />
                {config.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Model</Label>
                    <Select
                      value={config.model}
                      onValueChange={(value) => updateConfig(feature, 'model', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {modelOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center justify-between w-full">
                              <span>{option.label}</span>
                              <div className="flex gap-1 ml-2">
                                <Badge variant="secondary" className="text-xs">
                                  <Zap className="h-3 w-3 mr-1" />
                                  {option.speed}
                                </Badge>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {getModelInfo(config.model) && (
                      <div className="flex gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          {getModelInfo(config.model)?.cost}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {getModelInfo(config.model)?.speed}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Max Tokens</Label>
                    <Input
                      type="number"
                      value={config.maxTokens}
                      onChange={(e) => updateConfig(feature, 'maxTokens', parseInt(e.target.value))}
                      min={100}
                      max={4000}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Temperature ({config.temperature})</Label>
                    <Slider
                      value={[config.temperature]}
                      onValueChange={([value]) => updateConfig(feature, 'temperature', value)}
                      max={2}
                      min={0}
                      step={0.1}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Higher values make output more random
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Top P ({config.topP})</Label>
                    <Slider
                      value={[config.topP]}
                      onValueChange={([value]) => updateConfig(feature, 'topP', value)}
                      max={1}
                      min={0}
                      step={0.1}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Controls diversity via nucleus sampling
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Frequency Penalty ({config.frequencyPenalty})</Label>
                  <Slider
                    value={[config.frequencyPenalty]}
                    onValueChange={([value]) => updateConfig(feature, 'frequencyPenalty', value)}
                    max={2}
                    min={-2}
                    step={0.1}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Reduces repetition of frequent tokens
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Presence Penalty ({config.presencePenalty})</Label>
                  <Slider
                    value={[config.presencePenalty]}
                    onValueChange={([value]) => updateConfig(feature, 'presencePenalty', value)}
                    max={2}
                    min={-2}
                    step={0.1}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Encourages talking about new topics
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}